import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ShoppingCart, Heart, ChevronLeft, Package, Shield, Truck, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [universe, setUniverse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const prodRes = await axios.get(`${API}/products/${id}`);
        setProduct(prodRes.data);
        
        // Track view
        axios.post(`${API}/view/product/${id}`).catch(() => {});
        
        if (prodRes.data.sizes?.length > 0) {
          setSelectedSize(prodRes.data.sizes[0]);
        }
        
        if (prodRes.data.universe_id) {
          const uniRes = await axios.get(`${API}/universes`);
          const uni = uniRes.data.find(u => u.id === prodRes.data.universe_id);
          setUniverse(uni);
        }
        
        if (user) {
          try {
            const wishRes = await axios.get(`${API}/wishlist`);
            setIsWishlisted(wishRes.data.some(w => w.product_id === id));
          } catch (e) {}
        }
      } catch (error) {
        toast.error("Product not found");
        navigate("/store");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, user, navigate]);

  const addToCart = async () => {
    if (!user) {
      toast.error("Please sign in to add to cart");
      navigate("/login");
      return;
    }
    try {
      await axios.post(`${API}/cart/add`, {
        product_id: product.id,
        quantity: quantity,
        size: selectedSize || null,
      });
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  const toggleWishlist = async () => {
    if (!user) {
      toast.error("Please sign in to add to wishlist");
      return;
    }
    try {
      if (isWishlisted) {
        const wishRes = await axios.get(`${API}/wishlist`);
        const item = wishRes.data.find(w => w.product_id === product.id);
        if (item) {
          await axios.delete(`${API}/wishlist/${item.id}`);
          setIsWishlisted(false);
          toast.success("Removed from wishlist");
        }
      } else {
        await axios.post(`${API}/wishlist`, { product_id: product.id });
        setIsWishlisted(true);
        toast.success("Added to wishlist");
      }
    } catch (error) {
      toast.error("Failed to update wishlist");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030712]">
        <Navbar />
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) return null;

  const categoryLabels = {
    tshirt: "T-Shirt",
    poster: "Poster",
    collectible: "Collectible",
    action_figure: "Action Figure",
    membership: "Membership",
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Navbar />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/store" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Back to Store
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="card-surface overflow-hidden">
              <img 
                src={product.image || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800"}
                alt={product.name}
                className="w-full aspect-square object-cover"
              />
            </div>

            {/* Product Info */}
            <div>
              <div className="mb-4">
                <span className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500">
                  {categoryLabels[product.category] || product.category}
                </span>
                {universe && (
                  <span className="text-xs text-slate-500 ml-2">• {universe.name}</span>
                )}
              </div>

              <h1 className="text-3xl sm:text-4xl font-black text-white font-['Outfit'] mb-4">
                {product.name}
              </h1>

              <p className="text-slate-300 leading-relaxed mb-6">
                {product.description}
              </p>

              <div className="text-3xl font-bold text-yellow-500 mb-6">
                R{product.price.toFixed(2)}
                {product.category === "membership" && product.duration_months > 0 && (
                  <span className="text-base text-slate-400 ml-2">
                    / {product.duration_months === 1 ? "month" : `${product.duration_months} months`}
                  </span>
                )}
              </div>

              {/* Size Selector */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="mb-6">
                  <label className="text-sm text-slate-300 block mb-2">Size</label>
                  <Select value={selectedSize} onValueChange={setSelectedSize}>
                    <SelectTrigger className="input-dark w-32" data-testid="size-select">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-white/10">
                      {product.sizes.map((size) => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Quantity Selector */}
              {product.category !== "membership" && (
                <div className="mb-6">
                  <label className="text-sm text-slate-300 block mb-2">Quantity</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center border border-white/10 text-white hover:bg-white/5"
                      data-testid="qty-decrease"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-12 text-center text-white font-medium">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="w-10 h-10 flex items-center justify-center border border-white/10 text-white hover:bg-white/5"
                      data-testid="qty-increase"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Stock Status */}
              <div className="mb-6">
                {product.stock > 0 ? (
                  <span className="text-emerald-500 text-sm flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {product.stock > 10 ? "In Stock" : `Only ${product.stock} left`}
                  </span>
                ) : (
                  <span className="text-red-500 text-sm">Out of Stock</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-4 mb-8">
                <Button
                  onClick={addToCart}
                  disabled={product.stock === 0}
                  className="btn-primary flex-1 py-3 flex items-center justify-center gap-2"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Add to Cart
                </Button>
                <Button
                  onClick={toggleWishlist}
                  variant="outline"
                  className={`px-4 ${isWishlisted ? "text-red-500 border-red-500" : "btn-secondary"}`}
                  data-testid="wishlist-btn"
                >
                  <Heart className={`w-5 h-5 ${isWishlisted ? "fill-current" : ""}`} />
                </Button>
              </div>

              {/* Features */}
              <div className="space-y-3 pt-6 border-t border-white/10">
                <div className="flex items-center gap-3 text-sm text-slate-400">
                  <Truck className="w-5 h-5 text-amber-500" />
                  Free shipping on orders over R500
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-400">
                  <Shield className="w-5 h-5 text-amber-500" />
                  30-day money-back guarantee
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductPage;
